import { defineComponent as _defineComponent } from 'vue'
import { computed, ref, watch } from "vue";
  import {
    formatDateDayMonthYear,
    formatDateDayMonthYearWithTime,
  } from "@/helpers/date";
  import { LotStatus } from "@/enums/LotStatus";
  import { EoiDetails, Lot } from "@/types/CustomerLotDetails";
  import { EoiStatus } from "@/enums/EoiStatus";
  import { getUser } from "@/features/useUser";
  import { LotActionInterface } from "@/types/LotActionInterface";
  import router from "@/router";
  import dayjs from "dayjs";

  interface Props {
    actions: LotActionInterface[];
    eoiDetails: EoiDetails;
    lot: Lot;
  }
  
export default /*@__PURE__*/_defineComponent({
  __name: 'CustomerLotActivity',
  props: {
    actions: {},
    eoiDetails: {},
    lot: {}
  },
  emits: ["completeEoi", "changeTab"],
  setup(__props: any, { expose: __expose, emit: __emit }) {
  __expose();

  const props = __props;
  const emits = __emit;
  const normalizedActions = ref([]);

  const isEoiDeclined = computed(() => {
    return [EoiStatus.DECLINED, EoiStatus.WAITING_FOR_DECLINED].includes(
      props.eoiDetails.status
    );
  });
  const isCustomerCreatedEoi = computed(() => {
    return props.actions?.some((action) => {
      return action.newValues?.applicants?.includes(getUser()._id);
    });
  });
  const isContractTerminated = ref(false);

  function actionsByLastApplicant(actions) {
    const lastApplicantIndex = actions.findIndex((item) => {
      return (
        item.changedParams.includes("applicants") &&
        item.newValues?.applicants?.includes(getUser()._id) &&
        item.applicantDetails.eoiId === props.eoiDetails.id
      );
    });

    return actions.slice(0, lastApplicantIndex + 1);
  }
  function actionsBeforeDecline(actions) {
    let indexOfActionHasApplicants = 0;
    const reversedCopy = actions.slice().reverse();

    return reversedCopy.reduce((accumulator, action, index) => {
      if (
        action.changedParams.includes("applicants") &&
        action.newValues?.applicants?.includes(getUser()._id)
      ) {
        indexOfActionHasApplicants = index;
      }

      if (indexOfActionHasApplicants && index >= indexOfActionHasApplicants) {
        return [action, ...accumulator];
      }

      return accumulator;
    }, []);
  }
  const setNormalizedActions = (actions: any) => {
    const data = [];

    if (props.eoiDetails.status === EoiStatus.TERMINATED) {
      data.push({
        title: "This lot is no longer available",
        icon: "declined-icon",
        action: {
          text: "View other lots",
          handler: onViewLots,
        },
        updatedAt: props.eoiDetails.updatedAt,
      });
    }

    if (
      [EoiStatus.PENDING_FOR_BILLING, EoiStatus.DRAFT].includes(
        props.eoiDetails.status
      )
    ) {
      data.push({
        title: "Review and complete a Contract Information Form",
        date: getDateEoiCard(),
        icon: "contract-icon",
        action: {
          text: "Complete",
          handler: onComplete,
        },
        updatedAt: props.eoiDetails.updatedAt,
      });
    }

    actions.forEach((action) => {
      setStateOfContractTerminated(action);
      action.changedParams.forEach((param) => {
        if (
          ![EoiStatus.PENDING_FOR_BILLING, EoiStatus.DRAFT].includes(
            props.eoiDetails.status
          )
        ) {
          if (
            param === "applicants" &&
            action.newValues.applicants.includes(getUser()._id) &&
            action.applicantDetails.eoiId === props.eoiDetails.id
          ) {
            data.push({
              title: "You submitted a Contract Information Form",
              date: getDate(action.updatedAt),
              icon: "folder-icon",
              action: {
                text: "Review",
                handler: onReview,
              },
              updatedAt: action.updatedAt,
            });
          }

          if (
            param === "status" &&
            ![LotStatus.EOI, LotStatus.AVAILABLE].includes(
              action.newValues.status
            )
          ) {
            data.push({
              title: `Lot status updated to ${action.newValues.status}`,
              date: getDate(action.updatedAt),
              icon:
                action.newValues.status === LotStatus.SETTLED
                  ? "confetti-icon"
                  : "check-icon",
              updatedAt: action.updatedAt,
            });
          }

          if (param === "price") {
            data.push({
              title: `Price updated to $${action.newValues.price.toLocaleString()}`,
              date: getDate(action.updatedAt),
              icon: "check-icon",
              updatedAt: action.updatedAt,
            });
          }

          if (param === "registrationDate") {
            data.push({
              title: "Registration date was updated",
              date: getDate(action.updatedAt),
              icon: "calendar-plus-icon",
              notice: `Registration date: ${formatDateDayMonthYear(
                action.newValues.registrationDate
              )}`,
              updatedAt: action.updatedAt,
            });
          }

          if (param === "contracts") {
            data.push({
              title: "Executed land contract was uploaded",
              date: getDate(action.updatedAt),
              icon: "up-icon",
              action: {
                text: "View Contracts",
                handler: onViewContract,
              },
              updatedAt: action.updatedAt,
            });
          }

          if (
            param === "eoiId" &&
            action.newValues.eoiId === props.eoiDetails.id
          ) {
            data.push({
              title: "Your Contract Information Form was approved",
              date: getDate(action.updatedAt),
              icon: "check-rounded-icon",
              updatedAt: action.updatedAt,
            });
          }

          if (param === "settlementDate" && action.newValues.settlementDate) {
            data.push({
              title: "The settlement date was updated",
              date: getDate(action.updatedAt),
              icon: "calendar-plus-icon",
              notice: `Settlement date: ${formatDateDayMonthYear(
                action.newValues.settlementDate
              )}`,
              updatedAt: action.updatedAt,
            });
          }

          if (param === "financeDueDate" && action.newValues.financeDueDate) {
            data.push({
              title: "The finance due date was updated",
              date: getDate(action.updatedAt),
              icon: "calendar-blank-icon",
              notice: `Finance due date: ${formatDateDayMonthYear(
                action.newValues.financeDueDate
              )}`,
              updatedAt: action.updatedAt,
            });
          }
        }
      });
    });

    if (isEoiDeclined.value) {
      data.unshift({
        title: "Your Contract Information Form was declined",
        icon: "declined-icon",
        date: formatDateDayMonthYearWithTime(
          props.eoiDetails?.refundData?.executionStartDate
        ),
        updatedAt: props.eoiDetails?.refundData?.executionStartDate,
      });
    }

    if (isEoiIsRefunded()) {
      data.unshift({
        title: isContractTerminated.value
          ? "Contract was terminated"
          : "Your Contract Information Form was withdrawn",
        icon: "declined-icon",
        date: getDateEoiCard(),
        description: props.lot.estate.isRefundable
          ? "The holding deposit will be refunded to you within 3-5 business days"
          : "",
        updatedAt: props.eoiDetails.updatedAt,
      });
    }

    if (isEoiDeclined.value || isEoiIsRefunded()) {
      normalizedActions.value = filterTimelineActionsIfEoiDeclined(data);
    } else {
      normalizedActions.value = data;
    }
  };

  function isEoiIsRefunded() {
    return [
      EoiStatus.WITHDRAWN,
      EoiStatus.REFUNDED,
      EoiStatus.REFUND_DECLINED,
    ].includes(props.eoiDetails.status as EoiStatus);
  }
  function setStateOfContractTerminated(action) {
    if (
      action.newLotStatus === LotStatus.AVAILABLE &&
      [LotStatus.CONTRACT_EXECUTED, LotStatus.UNCONDITIONAL].includes(
        action.previousLotStatus
      )
    ) {
      isContractTerminated.value = true;
    }
  }
  function getDate(value) {
    return formatDateDayMonthYearWithTime(value);
  }
  const getDateEoiCard = () => {
    return props.eoiDetails.updatedAt
      ? formatDateDayMonthYearWithTime(props.eoiDetails.updatedAt)
      : formatDateDayMonthYearWithTime(props.eoiDetails.createdAt);
  };

  function onReview() {
    emits("changeTab", "eoi-details");
  }
  function onViewLots() {
    router.push({
      name: "EstateMap",
      params: {
        estateId: props.lot.estate.id,
      },
    });
  }
  function onViewContract() {
    emits("changeTab", "contracts");
  }
  function onComplete() {
    emits("completeEoi");
  }
  const filterTimelineActionsIfEoiDeclined = (actions) => {
    return actions.reduce((accumulator, action) => {
      const date1 = dayjs(props.eoiDetails?.refundData?.executionStartDate);
      const date2 = dayjs(action.updatedAt);

      if (Math.sign(date1.diff(date2)) < 0) {
        return accumulator;
      } else {
        return [...accumulator, action];
      }
    }, []);
  };

  watch(
    () => props.actions,
    (value) => {
      if (value.length) {
        setNormalizedActions(
          actionsByLastApplicant(actionsBeforeDecline(value))
        );
      }
    },
    {
      immediate: true,
    }
  );

const __returned__ = { props, emits, normalizedActions, isEoiDeclined, isCustomerCreatedEoi, isContractTerminated, actionsByLastApplicant, actionsBeforeDecline, setNormalizedActions, isEoiIsRefunded, setStateOfContractTerminated, getDate, getDateEoiCard, onReview, onViewLots, onViewContract, onComplete, filterTimelineActionsIfEoiDeclined, computed, ref, watch, get formatDateDayMonthYear() { return formatDateDayMonthYear }, get formatDateDayMonthYearWithTime() { return formatDateDayMonthYearWithTime }, get LotStatus() { return LotStatus }, get EoiDetails() { return EoiDetails }, get Lot() { return Lot }, get EoiStatus() { return EoiStatus }, get getUser() { return getUser }, get LotActionInterface() { return LotActionInterface }, get router() { return router }, get dayjs() { return dayjs } }
Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true })
return __returned__
}

})